import React from "react";
import WelcomeSection from "./WelcomeSection.jsx";
import CloudProviderCard from "../molecules/CloudProviderCard.jsx";
import OverallStatsCard from "../molecules/OverallStatsCard.jsx";
import QuickAccessSection from "./QuickAccessSection.jsx";
import { useMultiCloudDashboard } from "@/hooks/useMultiCloudDashboard.js";
import { BRAND_COLORS } from "../../config/brandColors.js";

function DashboardContent() {
  const { 
    cloudProviderStats, 
    overallStats, 
    loading, 
    error,
    refreshData 
  } = useMultiCloudDashboard();

  if (loading) {
    return (
      <div className="space-y-8">
        <div 
          className="bg-white rounded-xl p-6 relative overflow-hidden"
          style={{ boxShadow: BRAND_COLORS.ui.shadowMedium }}
        >
          <WelcomeSection />
          {/* Loading shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div 
              className="animate-spin rounded-full h-12 w-12 border-4 border-transparent"
              style={{ 
                borderTopColor: BRAND_COLORS.primary.orange,
                borderRightColor: BRAND_COLORS.primary.orange 
              }}
            ></div>
            <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div 
          className="bg-white rounded-xl p-6"
          style={{ boxShadow: BRAND_COLORS.ui.shadowMedium }}
        >
          <WelcomeSection />
        </div>
        <div 
          className="rounded-xl p-6 border"
          style={{ 
            backgroundColor: `${BRAND_COLORS.support.darkOrange}10`,
            borderColor: `${BRAND_COLORS.support.darkOrange}30`,
            boxShadow: BRAND_COLORS.ui.shadowLight
          }}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg 
                className="h-5 w-5" 
                viewBox="0 0 20 20" 
                fill="currentColor"
                style={{ color: BRAND_COLORS.support.darkOrange }}
              >
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 
                className="text-sm font-medium"
                style={{ color: BRAND_COLORS.support.darkOrange }}
              >
                Error loading dashboard data
              </h3>
              <p 
                className="text-sm mt-1"
                style={{ color: BRAND_COLORS.ui.textSecondary }}
              >
                {error}
              </p>
              <button 
                onClick={refreshData}
                className="mt-2 text-sm underline hover:no-underline transition-all duration-200"
                style={{ color: BRAND_COLORS.support.darkOrange }}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div 
        className="bg-white rounded-xl p-6 relative overflow-hidden"
        style={{ boxShadow: BRAND_COLORS.ui.shadowMedium }}
      >
        {/* Decorative gradient */}
        <div 
          className="absolute top-0 right-0 w-32 h-32 opacity-5 rounded-full -translate-y-16 translate-x-16"
          style={{ background: BRAND_COLORS.gradients.orange }}
        ></div>
        
        <div className="relative z-10">
          <WelcomeSection />
          <p 
            className="mt-4 text-sm leading-relaxed"
            style={{ color: BRAND_COLORS.ui.textSecondary }}
          >
            Welcome to Cloud Engineering Services Portal. Monitor and manage your multi-cloud infrastructure efficiently.
          </p>
        </div>
      </div>
      {/* Quick Access Section */}
      <QuickAccessSection />
      {/* Multi-Cloud Overview */}
      <div 
        className="bg-white rounded-xl p-6"
        style={{ boxShadow: BRAND_COLORS.ui.shadowMedium }}
      >
        <div 
          className="border-b pb-4 mb-6"
          style={{ borderColor: BRAND_COLORS.ui.borderLight }}
        >
          <div className="flex items-center justify-between">
            <h2 
              className="text-xl font-semibold"
              style={{ color: BRAND_COLORS.primary.darkGray }}
            >
              Multi-Cloud Dashboard
            </h2>
            <button 
              onClick={refreshData}
              className="text-sm flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200 hover:shadow-sm"
              style={{ 
                color: BRAND_COLORS.primary.orange,
                backgroundColor: `${BRAND_COLORS.primary.orange}10`
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="font-medium">Refresh</span>
            </button>
          </div>
        </div>

        {/* Overall Stats Card */}
        <div className="mb-8">
          <OverallStatsCard {...overallStats} />
        </div>

        {/* Cloud Providers Grid - Mejorado para responsividad */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
          {cloudProviderStats.map((provider) => (
            <CloudProviderCard
              key={provider.id}
              id={provider.id}
              name={provider.name}
              logo={provider.logo}
              color={provider.color}
              activeAccounts={provider.activeAccounts}
              inactiveAccounts={provider.inactiveAccounts}
              totalAccounts={provider.totalAccounts}
              totalBudget={provider.totalBudget}
            />
          ))}
        </div>

        {/* Additional Info */}
        <div 
          className="mt-8 pt-6 border-t"
          style={{ borderColor: BRAND_COLORS.ui.borderLight }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0 text-sm">
            <span style={{ color: BRAND_COLORS.ui.textSecondary }}>
              Last updated: {new Date().toLocaleString()}
            </span>
            <div className="flex items-center space-x-2">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: BRAND_COLORS.support.green }}
              ></div>
              <span style={{ color: BRAND_COLORS.ui.textSecondary }}>
                Data refreshes automatically every 5 minutes
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default DashboardContent;
